/**
 * Google Places loader + address helpers for Create Account "Other" city.
 * Set REACT_APP_GOOGLE_PLACES_API_KEY in .env and restart the dev server.
 */

export const getGooglePlacesApiKey = () =>
  String(process.env.REACT_APP_GOOGLE_PLACES_API_KEY || "").trim()

export const hasGooglePlacesApiKey = () => Boolean(getGooglePlacesApiKey())

let loadPromise = null

export const loadGooglePlaces = () => {
  if (typeof window === "undefined") {
    return Promise.reject(new Error("no_window"))
  }

  if (window.google?.maps?.places) {
    return Promise.resolve(window.google)
  }

  const key = getGooglePlacesApiKey()
  if (!key) {
    return Promise.reject(new Error("missing_key"))
  }

  if (loadPromise) return loadPromise

  loadPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector("script[data-amok-google-places]")
    if (existing) {
      if (window.google?.maps?.places) {
        resolve(window.google)
        return
      }
      existing.addEventListener("load", () => {
        if (window.google?.maps?.places) resolve(window.google)
        else reject(new Error("load_failed"))
      })
      existing.addEventListener("error", () => {
        loadPromise = null
        reject(new Error("load_failed"))
      })
      return
    }

    const script = document.createElement("script")
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(
      key,
    )}&libraries=places`
    script.async = true
    script.defer = true
    script.dataset.amokGooglePlaces = "true"
    script.onload = () => {
      if (window.google?.maps?.places) resolve(window.google)
      else {
        loadPromise = null
        reject(new Error("load_failed"))
      }
    }
    script.onerror = () => {
      loadPromise = null
      reject(new Error("load_failed"))
    }
    document.head.appendChild(script)
  })

  return loadPromise
}

export const formatOtherAddressLabel = (address = {}) =>
  [address.city, address.state, address.country]
    .map(part => String(part || "").trim())
    .filter(Boolean)
    .join(", ")

const findComponent = (components, ...types) => {
  const match = (components || []).find(component =>
    types.some(type => (component.types || []).includes(type)),
  )
  return match?.long_name || ""
}

/** Map a Google Place result → { city, state, country }. */
export const parsePlaceAddress = place => {
  const components = place?.address_components || []
  const city =
    findComponent(components, "locality") ||
    findComponent(components, "postal_town") ||
    findComponent(components, "sublocality", "sublocality_level_1") ||
    findComponent(components, "administrative_area_level_2")
  const state = findComponent(components, "administrative_area_level_1")
  const country = findComponent(components, "country")

  return {
    city: String(city || "").trim(),
    state: String(state || "").trim(),
    country: String(country || "").trim(),
  }
}
